import React, { useContext, useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Pill from 'LuxKit/Pill'
import TextLink from 'LuxKit/TextLink'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import FormatCurrency from 'components/Common/FormatCurrency'
import GeoContext from 'contexts/geoContext'
import { convertAudToRegion } from 'luxPlus/lib/getRegionSaveAmountCopy'
import Image from 'components/Common/Image/Image'

const CarouselWrapper = styled.div`
  padding-bottom: ${rem(40)};
  padding-top: ${rem(40)};
`

const TileWrapper = styled.div`
  width: ${rem(320)};
  flex-shrink: 0;
`
// so that it only wraps the full content inside the pill
const PillWrapper = styled.div`
  align-self: flex-start;
`

const TileImageWrapper = styled.div`
  width: ${rem(68)};
  align-self: stretch;
  flex-shrink: 0;
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
`

interface Destination {
  countryId: 'indonesia' | 'maldives' | 'thailand' | 'newZealand'
  hotelCount: number
  topSavingAud: number
  imageId: string
  url: string
}

const destinationCountryMessages = defineMessages({
  indonesia: {
    id: 'sBNdfj2x1RbFN-iG1zjR',
    defaultMessage: 'Indonesia',
    description: 'Country label for the LuxPlus destination carousel tile',
  },
  maldives: {
    id: 'U7tCOSjXmwZGjiMqyVOX',
    defaultMessage: 'Maldives',
    description: 'Country label for the LuxPlus destination carousel tile',
  },
  thailand: {
    id: 'Xvyns-5ICnbv9wB1xdvS',
    defaultMessage: 'Thailand',
    description: 'Country label for the LuxPlus destination carousel tile',
  },
  newZealand: {
    id: '082OR0EFkvCY2Z8X6I8T',
    defaultMessage: 'New Zealand',
    description: 'Country label for the LuxPlus destination carousel tile',
  },
})

const DESTINATIONS: Array<Destination> = [
  {
    countryId: 'indonesia',
    hotelCount: 646,
    topSavingAud: 711,
    imageId: 'vg4uvt2y5dgkivujuba6',
    url: '/search?destinationName=Bali&destinationId=le_64930b7151b6bc3e289f37d5da62ac9f&adults=2&children=none&pages=2',
  },
  {
    countryId: 'maldives',
    hotelCount: 185,
    topSavingAud: 636,
    imageId: 'dapbr82l65bcj5iy6v8',
    url: '/search?destinationName=Maldives&destinationId=le_2723d092b63885e0d7c260cc007e8b9d&adults=2&children=none&pages=2',
  },
  {
    countryId: 'thailand',
    hotelCount: 1703,
    topSavingAud: 585,
    imageId: 'lids8h5rs9zj2xvkxr',
    url: '/search?destinationName=Thailand&destinationId=le_8f85517967795eeef66c225f7883bdcb&adults=2&children=none&pages=2',
  },
  {
    countryId: 'newZealand',
    hotelCount: 503,
    topSavingAud: 548,
    imageId: '4ezeb1xkmkcuawwm0cf8',
    url: '/search?destinationName=New+Zealand&destinationId=le_9fc3d7152ba9336a670e36d0ed79bc43&adults=2&children=none&pages=2',
  },
]

interface TileProps {
  destination: Destination
  regionCode: string
}

function LuxPlusDestinationTile({ destination, regionCode }: TileProps) {
  const intl = useIntl()
  const localSaving = convertAudToRegion(destination.topSavingAud, regionCode)
  const countryLabel = intl.formatMessage(destinationCountryMessages[destination.countryId])

  const hotelCountText = intl.formatMessage(
    {
      id: 'PihjKsl1Gsakvmdvd1Yk',
      defaultMessage: '{count} hotels with member prices',
      description: 'Number of hotels available with LuxPlus member pricing in a destination',
    },
    { count: intl.formatNumber(destination.hotelCount) },
  )

  const saveUpToLabel = intl.formatMessage({
    id: '1SGCv-zzLmu0JYdVu5u8',
    defaultMessage: 'Save up to',
    description: 'Label before the maximum savings amount for a destination',
  })

  const viewOffersLabel = intl.formatMessage({
    id: '1LORyj0eHaqJOkKOyzbg',
    defaultMessage: 'View offers',
    description: 'Link text to view available offers for a destination',
  })

  return (
    <TileWrapper>
      <Group direction="horizontal" gap={12} verticalAlign="start">
        <TileImageWrapper>
          <Image id={destination.imageId} fit="center" dpr={2} alt={countryLabel} />
        </TileImageWrapper>
        <Group direction="vertical" gap={4}>
          <BodyTextBlock variant="medium" weight="bold">{countryLabel}</BodyTextBlock>
          <CaptionTextBlock variant="large" colour="light-foreground-alt">
            {hotelCountText}
          </CaptionTextBlock>
          <PillWrapper>
            <Pill kind="primary" variant="success">{saveUpToLabel} <FormatCurrency value={localSaving} format="rounded" /></Pill>
          </PillWrapper>
          <TextLink size="small" to={destination.url}>{viewOffersLabel}</TextLink>
        </Group>
      </Group>
    </TileWrapper>
  )
}

function LuxPlusDestinationCarousel() {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)

  const headingText = intl.formatMessage(
    {
      id: 'malhSKbLRFECrc1Zz4yt',
      defaultMessage: 'Discover destinations with {programName} savings',
      description: 'Heading for the destination carousel section showing LuxPlus member savings',
    },
    { programName: LUXURY_PLUS.PROGRAM_NAME },
  )

  const tiles = useMemo(() => DESTINATIONS.map((destination) => (
    <LuxPlusDestinationTile key={destination.countryId} destination={destination} regionCode={currentRegionCode} />
  )), [currentRegionCode])

  return (
    <CarouselWrapper>
      <LayoutContainer>
        <Group direction="vertical" gap={24}>
          <HeadingTextBlock variant="heading5">
            {headingText}
          </HeadingTextBlock>
          <CardCarousel>
            {tiles}
          </CardCarousel>
        </Group>
      </LayoutContainer>
    </CarouselWrapper>
  )
}

export default LuxPlusDestinationCarousel
